import { useEffect, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, ArrowUp, ArrowDown } from "lucide-react";
import { parseNavLinks } from "@/hooks/useSiteContent";

interface Link { label: string; href: string; }

export function NavigationTab() {
  const [rowId, setRowId] = useState<string | null>(null);
  const [links, setLinks] = useState<Link[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    supabase.from("site_content").select("id,value").eq("key", "nav_links").maybeSingle().then(({ data }) => {
      if (data) {
        setRowId(data.id);
        setLinks(parseNavLinks(data.value));
      }
      setLoading(false);
    });
  }, []);

  function update(i: number, field: keyof Link, value: string) {
    setLinks((ls) => ls.map((l, idx) => idx === i ? { ...l, [field]: value } : l));
  }
  function add() { setLinks((ls) => [...ls, { label: "New Link", href: "#" }]); }
  function remove(i: number) { setLinks((ls) => ls.filter((_, idx) => idx !== i)); }
  function move(i: number, dir: -1 | 1) {
    const j = i + dir; if (j < 0 || j >= links.length) return;
    const copy = [...links]; [copy[i], copy[j]] = [copy[j], copy[i]]; setLinks(copy);
  }

  async function save() {
    if (!rowId) return;
    setSaving(true);
    const value = links.map((l) => `${l.label}|${l.href}`).join(",");
    const { error } = await supabase.from("site_content").update({ value }).eq("id", rowId);
    setSaving(false);
    if (error) toast.error(error.message); else toast.success("Navigation saved");
  }

  if (loading) return <p className="text-muted-foreground">Loading...</p>;

  return (
    <div className="space-y-4">
      <div>
        <h2 className="text-2xl">Navigation Menu</h2>
        <p className="text-sm text-muted-foreground">
          Add, remove or reorder header menu links. Use <code>#section-id</code> for in-page links
          (e.g. <code>#services</code>, <code>#testimonials</code>, <code>#gallery</code>, <code>#book</code>, <code>#contact</code>),
          or a full URL like <code>https://...</code>.
        </p>
      </div>

      <div className="space-y-2">
        {links.map((l, i) => (
          <div key={i} className="flex flex-wrap items-end gap-2 rounded-lg border border-border bg-card p-3">
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs uppercase text-muted-foreground">Label</Label>
              <Input value={l.label} onChange={(e) => update(i, "label", e.target.value)} className="mt-1" />
            </div>
            <div className="flex-1 min-w-[140px]">
              <Label className="text-xs uppercase text-muted-foreground">Link (e.g. #services)</Label>
              <Input value={l.href} onChange={(e) => update(i, "href", e.target.value)} className="mt-1" />
            </div>
            <div className="flex gap-1">
              <Button type="button" size="icon" variant="outline" onClick={() => move(i, -1)} disabled={i === 0}><ArrowUp className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="outline" onClick={() => move(i, 1)} disabled={i === links.length - 1}><ArrowDown className="h-4 w-4" /></Button>
              <Button type="button" size="icon" variant="destructive" onClick={() => remove(i)}><Trash2 className="h-4 w-4" /></Button>
            </div>
          </div>
        ))}
      </div>

      <div className="flex gap-2">
        <Button variant="outline" onClick={add}><Plus className="mr-1 h-4 w-4" /> Add link</Button>
        <Button onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Navigation"}</Button>
      </div>
    </div>
  );
}
